import type { UserClass, RoleSerializerWithPermissions } from "../api-client/models";
import { PermissionConfig } from "../config/permissions";

export interface PermissionResult {
  success: boolean;
  message: string;
}

const DISABLE_SELF_APPROVAL = true;

// Get roles that have a specific permission
export const getRolesWithPermission = (
  roles: RoleSerializerWithPermissions[],
  permission: string,
  join: string = "or",
): string => {
  const roleNames = roles
    .filter((role) => role.permissions && role.permissions.indexOf(permission) > -1)
    .map((role) => role.name);

  if (roleNames.length === 0) {
    return "";
  }

  if (roleNames.length === 1) {
    return roleNames[0];
  }

  return `${roleNames.slice(0, -1).join(", ")} ${join} ${roleNames[roleNames.length - 1]}`;
};

// Check if user can self-approve based on having multiple roles with the approval permission
export const canUserSelfApprove = (
  user: UserClass,
  roles: RoleSerializerWithPermissions[],
  approvePermission: string,
): boolean => {
  if (!DISABLE_SELF_APPROVAL) return true;

  // Count how many of the user's roles have the approval permission
  const rolesWithApprovalPermission = roles.filter((role) => {
    const hasApprovalPerm =
      role.permissions && role.permissions.indexOf(approvePermission) > -1;

    const userHasRole =
      user.roles && user.roles.some((userRole) => userRole.id === role.id);

    return hasApprovalPerm && userHasRole;
  });

  // User can self-approve if they have 2 or more roles with the approval permission
  return rolesWithApprovalPermission.length >= 2;
};

// Generic permission checker
export const checkPermission = (
  user: UserClass | undefined,
  roles: RoleSerializerWithPermissions[],
  config: PermissionConfig,
  adminOn: boolean,
  ...args: any[]
): PermissionResult => {
  if (!user) {
    return { success: false, message: "Invalid User" };
  }

  // Admin bypass
  if (adminOn && user.isAdmin) {
    return { success: true, message: "" };
  }

  // Check if user has the required permission
  if (config.key && !user.permissions?.[config.key]) {
    const rolesWithPerm = getRolesWithPermission(roles, config.key);
    return {
      success: false,
      message: config.errorMessage(rolesWithPerm),
    };
  }

  // Additional checks (like ownership, prerequisites, etc.)
  if (config.additionalCheck) {
    const additionalError = config.additionalCheck(user, ...args);
    if (additionalError) {
      // Check if it's a REQUIRES: directive
      if (additionalError.startsWith('REQUIRES:')) {
        const requiredPermission = additionalError.replace('REQUIRES:', '');
        const rolesWithPerm = getRolesWithPermission(roles, requiredPermission);
        return {
          success: false,
          message: `The ${rolesWithPerm} must perform this action`,
        };
      }
      return { success: false, message: additionalError };
    }
  }

  // Self-approval checks
  if (config.requiresSelfApproval) {
    const createdBy = config.requiresSelfApproval.createdByCheck(user, ...args);
    if (
      user.id === createdBy &&
      !canUserSelfApprove(user, roles, config.requiresSelfApproval.permission)
    ) {
      const rolesWithPerm = getRolesWithPermission(
        roles,
        config.requiresSelfApproval.permission,
      );
      return {
        success: false,
        message: config.requiresSelfApproval.errorMessage(rolesWithPerm),
      };
    }
  }

  return { success: true, message: "" };
};

// Simple helper to check if user has a specific permission
export const hasPermission = (user: UserClass, permission: string): boolean => {
  return !!(user.permissions && user.permissions[permission]);
};
